import { supabase } from "@/integrations/supabase/client";

/**
 * Storage bucket names used in the application
 */
export type BucketName = 
  | "avatars" 
  | "pitch-decks" 
  | "course-content" 
  | "gallery" 
  | "announcements-media" 
  | "attachments"
  | "homework-files";

/**
 * Thumbnail size options for image rendering
 */
export type ThumbnailSize = 48 | 64 | 128 | 256;

/**
 * Generates a public URL from a storage path
 * Returns null if path is null/undefined
 */
export function getStorageUrl(
  bucket: BucketName, 
  path: string | null | undefined
): string | null {
  if (!path) return null;
  
  // If it's already a full URL (legacy), return as-is
  if (path.startsWith("http")) return path;
  
  const { data: { publicUrl } } = supabase.storage
    .from(bucket)
    .getPublicUrl(path);
  
  return publicUrl;
}

/**
 * Generates a thumbnail URL using Supabase's image transformation API.
 * Uses render endpoint for on-the-fly resizing.
 */
export function getThumbnailUrl(
  bucket: BucketName,
  path: string | null | undefined,
  size: ThumbnailSize = 64
): string | null {
  if (!path) return null;
  
  // If it's already a full URL (legacy), append transform params
  if (path.startsWith("http")) {
    // Check if it's a Supabase storage URL we can transform
    if (path.includes("/storage/v1/object/public/")) {
      // Convert to render URL with resize params
      return path.replace(
        "/storage/v1/object/public/",
        `/storage/v1/render/image/public/`
      ) + `?width=${size}&height=${size}&resize=cover`;
    }
    return path;
  }
  
  // Use Supabase's image transformation endpoint
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  return `${supabaseUrl}/storage/v1/render/image/public/${bucket}/${path}?width=${size}&height=${size}&resize=cover`;
}

/**
 * Extracts the storage path from a full URL
 * Used for data migration
 */
export function extractPathFromUrl(
  url: string | null | undefined,
  bucket: BucketName
): string | null {
  if (!url) return null;
  
  // If it's already a path (not a URL), return as-is
  if (!url.startsWith("http")) return url;
  
  // Match pattern: .../storage/v1/object/public/{bucket}/{path}
  const regex = new RegExp(
    `storage/v1/object/public/${bucket}/(.+)$`
  );
  const match = url.match(regex);
  
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Checks if a value is already a path (not a full URL)
 */
export function isStoragePath(value: string | null | undefined): boolean {
  if (!value) return true; // null/undefined is valid
  return !value.startsWith("http");
}

/**
 * Mints a short-lived signed URL for a private bucket and opens it in a new tab.
 *
 * `homework-files` is a PRIVATE bucket, so getStorageUrl()/getPublicUrl() cannot
 * read it — those return a URL that 400s. Use this instead for any private
 * bucket. Mirrors the pitch-deck download flow: check `error`, detect blocked
 * pop-ups, and never report success unless the tab actually opened.
 *
 * Returns null on success, or a human-readable reason on failure.
 */
export async function openSignedStorageUrl(
  bucket: BucketName,
  path: string | null | undefined,
  expiresInSeconds = 60
): Promise<string | null> {
  if (!path) return "No file attached.";

  // Legacy full URLs (pre-dating the private bucket) open directly.
  if (path.startsWith("http")) {
    return window.open(path, "_blank") ? null : "Your browser blocked the file window.";
  }

  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, expiresInSeconds);

  if (error || !data?.signedUrl) {
    console.error("Failed to create signed URL:", error);
    return "Could not open the file. It may have been removed.";
  }

  return window.open(data.signedUrl, "_blank") ? null : "Your browser blocked the file window.";
}
